"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useClientWorkspaceIdentifiers = exports.useClientWorkspaceError = exports.useClientWorkspaceStatus = exports.ResolvedClientWorkspaceProvider = exports.ClientWorkspaceProvider = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var BackendContext_1 = require("../BackendContext");
var useCancelablePromise_1 = require("../useCancelablePromise");
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var resolveLCMWorkspaceIdentifiers_1 = require("./resolveLCMWorkspaceIdentifiers");
var WorkspaceContext_1 = require("../WorkspaceContext");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var ClientWorkspaceContext = react_1.default.createContext({
    status: "pending",
    error: undefined,
    result: undefined,
});
ClientWorkspaceContext.displayName = "ClientWorkspace";
/**
 * ClientWorkspaceProvider can be used as a replacement of the {@link WorkspaceProvider},
 * if you want to work with the workspace in LCM context.
 *
 * It allows you to:
 * - Use dataProduct and client identifier as a replacement of the workspace identifier.
 *   Workspace identifier is resolved and provided to the {@link WorkspaceProvider} automatically.
 *
 * - Use workspace identifier to obtain dataProduct, client and segment identifiers by the {@link useClientWorkspaceIdentifiers} hooks.
 *
 * If the backend does not support clientId / dataProduct LCM provisioning,
 * or the workspace is not provisioned via LCM, segment / client / dataProduct values will be undefined.
 *
 * To read more details about LCM, see: {@link https://help.gooddata.com/pages/viewpage.action?pageId=86796865}
 *
 * @alpha
 */
exports.ClientWorkspaceProvider = function (props) {
    var _a;
    var children = props.children, customBackend = props.backend;
    var _b = getInputLCMIdentifiersFromProps(props), client = _b.client, dataProduct = _b.dataProduct, customWorkspace = _b.workspace;
    var backend = BackendContext_1.useBackendStrict(customBackend, "ClientWorkspaceProvider");
    var workspace = WorkspaceContext_1.useWorkspace(customWorkspace);
    var lcmIdentifiers = useCancelablePromise_1.useCancelablePromise({
        promise: function () { return resolveLCMWorkspaceIdentifiers_1.resolveLCMWorkspaceIdentifiers(backend, { client: client, dataProduct: dataProduct, workspace: workspace }); },
    }, [client, dataProduct, workspace, backend]);
    var ws = (_a = lcmIdentifiers.result) === null || _a === void 0 ? void 0 : _a.workspace;
    return (react_1.default.createElement(ClientWorkspaceContext.Provider, { value: lcmIdentifiers },
        react_1.default.createElement(WorkspaceContext_1.WorkspaceProvider, { workspace: ws }, children)));
};
/**
 * ResolvedClientWorkspaceProvider can be used as a replacement of the {@link WorkspaceProvider}, if you are accessing
 * workspace in LCM context.
 *
 * This provider expects that the client workspace is already resolved on input to the provider. The provider
 * will then establish a client workspace and workspace contexts so that the resolved information can
 * be accessed by the children.
 *
 * Note: check out the {@link ClientWorkspaceProvider} for version of provider that performs the resolution of
 * client workspace identifiers to workspace.
 *
 * @alpha
 */
exports.ResolvedClientWorkspaceProvider = function (props) {
    ts_invariant_1.default(props.dataProduct);
    ts_invariant_1.default(props.client);
    ts_invariant_1.default(props.workspace);
    var context = {
        status: "success",
        result: props,
        error: undefined,
    };
    return (react_1.default.createElement(ClientWorkspaceContext.Provider, { value: context },
        react_1.default.createElement(WorkspaceContext_1.WorkspaceProvider, { workspace: props.workspace }, props.children)));
};
/**
 * Hook to obtain loading status of the {@link ClientWorkspaceProvider} - "success", "error", "loading" or "pending".
 * @alpha
 */
exports.useClientWorkspaceStatus = function () {
    var context = react_1.default.useContext(ClientWorkspaceContext);
    return context.status;
};
/**
 * Hook to obtain loading error of the {@link ClientWorkspaceProvider}.
 * @alpha
 */
exports.useClientWorkspaceError = function () {
    var context = react_1.default.useContext(ClientWorkspaceContext);
    return context.error;
};
/**
 * Hook to obtain all resolved identifiers from the {@link ClientWorkspaceProvider} - workspace, segment, dataProduct and client.
 * @alpha
 */
exports.useClientWorkspaceIdentifiers = function () {
    var _a;
    var context = react_1.default.useContext(ClientWorkspaceContext);
    return (_a = context.result) !== null && _a !== void 0 ? _a : {};
};
//
//
//
function hasWorkspaceProp(obj) {
    return !isEmpty_1.default(obj) && !!obj.workspace;
}
function getInputLCMIdentifiersFromProps(props) {
    if (hasWorkspaceProp(props)) {
        return {
            workspace: props.workspace,
        };
    }
    ts_invariant_1.default(props.client && props.dataProduct, "ClientWorkspaceProvider: You must specify either - the workspace identifier, or the client and dataProduct identifier.");
    return {
        client: props.client,
        dataProduct: props.dataProduct,
    };
}
//# sourceMappingURL=ClientWorkspaceContext.js.map